import type { FlightHydrated } from "@ca/db/flights";
import type { FlightEvent } from "@ca/db/schema";
import type { Nullish } from "@ca/types/util";
import type { MaybeRefOrGetter } from "vue";

import { computed, toValue } from "vue";

import { formatDuration } from "@ca/formatters/dates";

import { useFlightApproach } from "@/queries/flights";
import { toSinkRateRuns } from "@/utils/sink_rate";

export const useFlightDuration = (flight: Nullish<FlightHydrated>) => {
	return computed(() => {
		if (!flight) {
			return null;
		}
		const departureTime = flight.departureTime ?? flight.filedDepartureTime;
		const arrivalTime = flight.arrivalTime ?? flight.filedArrivalTime;
        
		return formatDuration(departureTime, arrivalTime);
	});
};

export const useFlightDurations = (flight: Nullish<FlightHydrated>) => {
	return computed(() => {
		if (!flight) {
			return null;
		}
		const actual = formatDuration(flight.departureTime, flight.arrivalTime);
		const planned = formatDuration(flight.filedDepartureTime, flight.filedArrivalTime);
        
		return {
			actual,
			planned,
			match: actual === planned,
		};
	});
};

type UseFlightApproachDataOptions = {
	events: MaybeRefOrGetter<FlightEvent[]>;
	flightId: string;
	landingAirportElevation?: MaybeRefOrGetter<number | null | undefined>;
};

export const escapeHtml = (str: string): string => {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
};

export const useFlightApproachData = ({
	events,
	flightId,
	landingAirportElevation,
}: UseFlightApproachDataOptions) => {
	const { data: approachData } = useFlightApproach(flightId);

	const approach = computed(() => approachData.value?.samples ?? []);

	const sinkRateRuns = computed(() => toSinkRateRuns(toValue(events)));

	const groundReference = computed(() => approachData.value?.groundReference ?? null);

	const airportElevation = computed(() => (
		groundReference.value?.referenceAltitudeFt ?? toValue(landingAirportElevation ?? null) ?? null
	));

	const datumOffsetFt = computed(() => groundReference.value?.datumOffsetFt ?? 0);

	const touchdownElevation = computed(() => (
		groundReference.value?.touchdownElevationFt ?? toValue(landingAirportElevation ?? null) ?? null
	));

	const toAglAltitude = (altitude: number | null) => {
		const elevation = airportElevation.value;
		if (altitude === null || elevation === null) {
			return null;
		}
		if (altitude === 0) {
			return 0;
		}
		return altitude - elevation;
	};

	const toMslAltitude = (altitude: number | null) => {
		if (altitude === null) {
			return null;
		}
		if (altitude === 0) {
			return touchdownElevation.value;
		}
		return altitude - datumOffsetFt.value;
	};

	return {
		airportElevation,
		approach,
		datumOffsetFt,
		groundReference,
		sinkRateRuns,
		toAglAltitude,
		toMslAltitude,
		touchdownElevation,
	};
};